// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchFundInvestorsFiltered,
  fetchFundInvestorsFilterOptions,
  type FundInvestorsPaginatedResponse
} from '../services/fundsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  getSubPreferenceRow,
  PREF_SUBKEY_FUND_INVESTORS,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow,
  type FilterValue,
  type SortConfig
} from '../../global_configs/tableViewPreferences.ts'
import { MENU_ITEM_KEY_FUNDS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { useFundInvestorsReportId } from '../../../state_manager/hooks/useFundInvestorsReportId.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'

const DEFAULT_INVESTORS_FILTERS: FilterValue[] = []

/**
 * useFundInvestors — local-state hook for the per-fund Investors table.
 * Mirrors useFundInvestmentSummary in shape (sub-key user preferences under
 * the funds menu_item; local state for active filters / sorting so multiple
 * fund tabs don't share). The Investors table has no aggregations, no
 * as-of date, no warehoused/converted-out toggles — it's a flat list of
 * fund_commitment rows.
 */
export function useFundInvestors(fundId: string | null | undefined) {
  const dispatch = useDispatch<AppDispatch>()
  const recordIdString = fundId ? String(fundId) : null

  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUNDS
  )

  const fundInvestorsReportId = useFundInvestorsReportId()
  const defaultTableSorting = useReportingDefaultSorting(fundInvestorsReportId)

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [activeFilters, setActiveFiltersLocal] = useState<FilterValue[]>(DEFAULT_INVESTORS_FILTERS)
  const [activeSorting, setActiveSortingLocal] = useState<SortConfig[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  // 1. Hydrate filters / sorting from prefs (under the investors sub-key).
  //    Re-runs when defaultTableSorting populates (after globalConfigs loads)
  //    so a user with no persisted sort picks up the cfg-driven default once
  //    `reporting_default_sorts` arrives in the slice.
  useEffect(() => {
    const sub = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTORS)
    const nextFilters = resolveTableFiltersFromPreferenceRow(sub, DEFAULT_INVESTORS_FILTERS)
    const nextSorting = resolveTableSortingFromPreferenceRow(sub, defaultTableSorting)
    if (!areFilterValuesEqual(nextFilters, activeFilters)) setActiveFiltersLocal(nextFilters)
    if (!areSortConfigsEqual(nextSorting, activeSorting)) setActiveSortingLocal(nextSorting)
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceRow, defaultTableSorting])

  // 2. Fetch filter options once we have a fund id.
  useEffect(() => {
    if (!recordIdString) {
      setFilterOptions({})
      return
    }
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundInvestorsFilterOptions(recordIdString, token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordIdString])

  // 3. Fetch data on filter/sort/fundId change (after prefs are ready).
  const fetchPage1 = useCallback(
    async (sorting: SortConfig[], filters: FilterValue[]) => {
      if (!recordIdString) return
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundInvestorsPaginatedResponse = await fetchFundInvestorsFiltered(
          recordIdString,
          sorting,
          filters,
          1,
          null,
          token
        )
        if (res.error) {
          setError(res.error)
          setData([])
          setHasMore(false)
        } else {
          setData(res.data)
          setHasMore(res.hasMore)
          setCurrentPage(1)
        }
      } finally {
        setLoading(false)
      }
    },
    [recordIdString]
  )

  useEffect(() => {
    if (!prefsReady || !recordIdString) return
    void fetchPage1(activeSorting, activeFilters)
  }, [prefsReady, recordIdString, activeFilters, activeSorting, fetchPage1])

  // 4. Persist filter/sort changes to user prefs (under the sub-key).
  const persistMergedSubPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const baseSubData = (baseData[PREF_SUBKEY_FUND_INVESTORS] as Record<string, unknown>) || {}
      const newSubData: Record<string, unknown> = {
        ...baseSubData,
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const merged: Record<string, unknown> = {
        ...baseData,
        [PREF_SUBKEY_FUND_INVESTORS]: newSubData
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedSubPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist fund investors preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedSubPreference])

  // 5. Public handlers.
  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      setActiveFiltersLocal(filters)
    },
    [activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      setActiveSortingLocal(sorting)
    },
    [activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    setActiveFiltersLocal([])
  }, [])

  const handleClearSorting = useCallback(() => {
    setActiveSortingLocal(defaultTableSorting)
  }, [defaultTableSorting])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      setActiveFiltersLocal(DEFAULT_INVESTORS_FILTERS)
      setActiveSortingLocal(defaultTableSorting)
      return
    }
    void (async () => {
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const merged = { ...baseData }
      delete (merged as any)[PREF_SUBKEY_FUND_INVESTORS]
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        skipPersistRef.current = true
        setActiveFiltersLocal(DEFAULT_INVESTORS_FILTERS)
        setActiveSortingLocal(defaultTableSorting)
      }
    })()
  }, [menuItemId, defaultTableSorting, persistPreference, dispatch])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedSubPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedSubPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const baseData = (row?.data ?? {}) as Record<string, unknown>
    const baseSubData = ((baseData[PREF_SUBKEY_FUND_INVESTORS] as Record<string, unknown>) || {})
    const newSubData = { ...baseSubData }
    delete (newSubData as any)[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...baseData,
      [PREF_SUBKEY_FUND_INVESTORS]: {
        ...newSubData,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      dispatch(mergeUserPreferenceRowData(menuItemId, merged))
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const refetch = useCallback(() => fetchPage1(activeSorting, activeFilters), [fetchPage1, activeSorting, activeFilters])

  const subPreferenceRow = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTORS)

  return {
    data,
    loading,
    error,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow: subPreferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters: DEFAULT_INVESTORS_FILTERS,
    defaultTableSorting
  }
}
